// Multi-territory Entity Master API service
import {
  MT_COUNTRY_DROPDOWN_API,
  MT_ENTITY_DELETE_API,
  MT_ENTITY_DOWNLOAD_API,
  MT_ENTITY_LIST_API,
  MT_ENTITY_SAVE_API,
  MT_ENTITY_UPDATE_API,
} from '@/multi-territory/mt-services/mt-api-endpoints'
import {
  MTApiResponse,
  mtBuildRequestHeader,
  mtHandleErrorResponse,
  mtHandleRequestFailure,
  mtParseJsonResponse,
  mtParseMessageResponse,
  mtUrlBuilder,
} from '@/multi-territory/mt-services/mt-api-utils'
import type {
  MTCountryOption,
  MTEntityItem,
  MTEntityListRequest,
  MTEntityListResponse,
  MTEntitySaveRequest,
  MTEntityUpdateRequest,
} from '@/multi-territory/mt-types/mt-admin'

// Re-export types for consumer convenience
export type {
  MTCountryOption,
  MTEntityItem,
  MTEntityListRequest,
  MTEntityListResponse,
  MTEntitySaveRequest,
  MTEntityUpdateRequest
}

// --- API Functions ---

/**
 * Fetch country dropdown list for entity form.
 * GET /wht-mt-admin/api/v1/countrymaster/dropdown
 */
export const getEntityCountryDropdown = async (): Promise<
  MTApiResponse<MTCountryOption[]>
> => {
  const url = mtUrlBuilder(MT_COUNTRY_DROPDOWN_API)
  const requestInit = mtBuildRequestHeader('GET')
  try {
    const response = await fetch(url, requestInit)
    if (!response.ok) return await mtHandleRequestFailure(response)
    return await mtParseJsonResponse<MTCountryOption[]>(response)
  } catch (error) {
    return mtHandleErrorResponse(error)
  }
}

/**
 * Fetch entity master listing with pagination and optional search filters.
 * POST /wht-admin/api/v1/entity-master/list/{countryCode}
 * If countryCode is not provided, fetches all entities across countries.
 */
export const getEntityMasterList = async (
  countryCode: string | undefined,
  payload: MTEntityListRequest
): Promise<MTApiResponse<MTEntityListResponse>> => {
  const url = mtUrlBuilder(MT_ENTITY_LIST_API(countryCode))
  const requestInit = mtBuildRequestHeader('POST', payload)
  try {
    const response = await fetch(url, requestInit)
    if (!response.ok) return await mtHandleRequestFailure(response)
    return await mtParseJsonResponse<MTEntityListResponse>(response)
  } catch (error) {
    return mtHandleErrorResponse(error)
  }
}

/**
 * Create a new entity master record.
 * POST /wht-admin/api/v1/entity-master/save
 */
export const saveEntityMaster = async (
  payload: MTEntitySaveRequest
): Promise<MTApiResponse<string>> => {
  const url = mtUrlBuilder(MT_ENTITY_SAVE_API)
  const requestInit = mtBuildRequestHeader('POST', payload)
  try {
    const response = await fetch(url, requestInit)
    if (!response.ok) return await mtHandleRequestFailure(response)
    return await mtParseMessageResponse(response)
  } catch (error) {
    return mtHandleErrorResponse(error)
  }
}

/**
 * Download entity master data as Excel.
 * POST /wht-admin/api/v1/entity-master/download
 */
export const downloadEntityMaster = async (
  payload: MTEntityListRequest
): Promise<MTApiResponse<Blob>> => {
  const url = mtUrlBuilder(MT_ENTITY_DOWNLOAD_API)
  const requestInit = mtBuildRequestHeader('POST', payload)
  try {
    const response = await fetch(url, requestInit)
    if (!response.ok) return await mtHandleRequestFailure(response)
    const blob = await response.blob()
    return { success: true, data: blob }
  } catch (error) {
    return mtHandleErrorResponse(error)
  }
}

/**
 * Delete an entity master record by UUID.
 * DELETE /wht-admin/api/v1/entity-master/{uuid}
 */
export const deleteEntityMaster = async (
  uuid: string
): Promise<MTApiResponse<string>> => {
  const url = mtUrlBuilder(`${MT_ENTITY_DELETE_API}/${uuid}`)
  const requestInit = mtBuildRequestHeader('DELETE')
  try {
    const response = await fetch(url, requestInit)
    if (!response.ok) return await mtHandleRequestFailure(response)
    return await mtParseMessageResponse(response)
  } catch (error) {
    return mtHandleErrorResponse(error)
  }
}

/**
 * Update an existing entity master record.
 * PUT /wht-admin/api/v1/entity-master/update
 */
export const updateEntityMaster = async (
  payload: MTEntityUpdateRequest
): Promise<MTApiResponse<string>> => {
  const url = mtUrlBuilder(MT_ENTITY_UPDATE_API)
  const requestInit = mtBuildRequestHeader('PUT', payload)
  try {
    const response = await fetch(url, requestInit)
    if (!response.ok) return await mtHandleRequestFailure(response)
    return await mtParseMessageResponse(response)
  } catch (error) {
    return mtHandleErrorResponse(error)
  }
}
